import { useState, useCallback } from 'react';
import { translateText, type TranslationResult } from '@/lib/gemini';
import { useUserLanguage } from './use-user-language';
import { useAuth } from './use-auth';
import { apiUrl } from '@/lib/config';

const translations = {
  en: {
    // Common
    'loading': 'Loading...',
    'cancel': 'Cancel',
    'continue': 'Continue',
    'save': 'Save',
    'close': 'Close',
    'settings': 'Settings',
    'admin': 'Admin',

    // Chat
    'new_chat': 'New Chat',
    'new_group': 'New Group',
    'create_chat': 'Create Chat',
    'create_group': 'Create Group',
    'chat_name': 'Chat name',
    'group_name': 'Group name',
    'enter_chat_name': 'Enter chat name...',
    'enter_group_name': 'Enter group name...',
    'send_message': 'Send a message...',
    'no_messages': 'No messages yet',
    'start_conversation': 'Send a message to start the conversation!',
    'select_chat': 'Select a chat to start messaging',
    'welcome_traveler_chat': 'Welcome to Vyzle Chat',
    'online': 'online',
    'members': 'members',
    'typing': 'typing...',
    'auto_translate': 'Auto Translate',
    'translation_enabled': 'Translation enabled',
    'language': 'Language',
    'show_original': 'Show original',
    'show_translation': 'Show translation',
    'translating': 'Translating...',
    'translated_to': 'Translated to',
    'translation_failed': 'Translation failed',
    'translation_unavailable': 'Translation unavailable',

    // Language selection
    'choose_language': 'Choose your preferred language',
    'language_description': 'All messages will be automatically translated for you.',
    'continue_to_chat': 'Continue to Chat',

    // Auth
    'setting_up_auth': 'Setting up authentication...',
    'authenticating': 'Authenticating user...',
    'auth_error': 'Authentication Error',
    'retry_auth': 'Retry Authentication',
    'fallback_auth': 'Using fallback authentication (Firebase not configured)',
    'demo_mode': 'Using demo mode (Firebase not configured)',

    // Errors
    'invalid_name': 'Invalid Name',
    'enter_valid_name': 'Please enter a valid name',
    'auth_required': 'Authentication Required',
    'failed_create': 'Failed to create',
    'unknown_error': 'Unknown error occurred',
  },

  es: {
    // Common
    'loading': 'Cargando...',
    'cancel': 'Cancelar',
    'continue': 'Continuar',
    'save': 'Guardar',
    'close': 'Cerrar',
    'settings': 'Configuración',
    'admin': 'Administrador',

    // Chat
    'new_chat': 'Nuevo Chat',
    'new_group': 'Nuevo Grupo',
    'create_chat': 'Crear Chat',
    'create_group': 'Crear Grupo',
    'chat_name': 'Nombre del chat',
    'group_name': 'Nombre del grupo',
    'enter_chat_name': 'Ingresa el nombre del chat...',
    'enter_group_name': 'Ingresa el nombre del grupo...',
    'send_message': 'Envía un mensaje...',
    'no_messages': 'Sin mensajes aún',
    'start_conversation': '¡Envía un mensaje para comenzar la conversación!',
    'select_chat': 'Selecciona un chat para comenzar a conversar',
    'welcome_traveler_chat': 'Bienvenido a Vyzle Chat',
    'online': 'en línea',
    'members': 'miembros',
    'typing': 'escribiendo...',
    'auto_translate': 'Traducción Automática',
    'translation_enabled': 'Traducción habilitada',
    'language': 'Idioma',
    'show_original': 'Mostrar original',
    'show_translation': 'Mostrar traducción',
    'translating': 'Traduciendo...',
    'translated_to': 'Traducido al',
    'translation_failed': 'Traducción falló',
    'translation_unavailable': 'Traducción no disponible',

    // Language selection
    'choose_language': 'Elige tu idioma preferido',
    'language_description': 'Todos los mensajes serán traducidos automáticamente para ti.',
    'continue_to_chat': 'Continuar al Chat',

    // Auth
    'setting_up_auth': 'Configurando autenticación...',
    'authenticating': 'Autenticando usuario...',
    'auth_error': 'Error de Autenticación',
    'retry_auth': 'Reintentar Autenticación',
    'fallback_auth': 'Usando autenticación de respaldo (Firebase no configurado)',
    'demo_mode': 'Usando modo demo (Firebase no configurado)',

    // Errors
    'invalid_name': 'Nombre Inválido',
    'enter_valid_name': 'Por favor ingresa un nombre válido',
    'auth_required': 'Autenticación Requerida',
    'failed_create': 'Falló al crear',
    'unknown_error': 'Error desconocido ocurrió',
  },

  fr: {
    // Common
    'loading': 'Chargement...',
    'cancel': 'Annuler',
    'continue': 'Continuer',
    'save': 'Sauvegarder',
    'close': 'Fermer',
    'settings': 'Paramètres',
    'admin': 'Admin',

    // Chat
    'new_chat': 'Nouveau Chat',
    'new_group': 'Nouveau Groupe',
    'create_chat': 'Créer un Chat',
    'create_group': 'Créer un Groupe',
    'chat_name': 'Nom du chat',
    'group_name': 'Nom du groupe',
    'enter_chat_name': 'Entrez le nom du chat...',
    'enter_group_name': 'Entrez le nom du groupe...',
    'send_message': 'Envoyer un message...',
    'no_messages': 'Aucun message encore',
    'start_conversation': 'Envoyez un message pour commencer la conversation!',
    'select_chat': 'Sélectionnez un chat pour commencer à converser',
    'welcome_traveler_chat': 'Bienvenue sur Vyzle Chat',
    'online': 'en ligne',
    'members': 'membres',
    'typing': 'en train de taper...',
    'auto_translate': 'Traduction Automatique',
    'translation_enabled': 'Traduction activée',
    'language': 'Langue',
    'show_original': 'Afficher l\'original',
    'show_translation': 'Afficher la traduction',
    'translating': 'Traduction en cours...',
    'translated_to': 'Traduit en',
    'translation_failed': 'Traduction échouée',
    'translation_unavailable': 'Traduction indisponible',

    // Language selection
    'choose_language': 'Choisissez votre langue préférée',
    'language_description': 'Tous les messages seront automatiquement traduits pour vous.',
    'continue_to_chat': 'Continuer vers le Chat',

    // Auth
    'setting_up_auth': 'Configuration de l\'authentification...',
    'authenticating': 'Authentification de l\'utilisateur...',
    'auth_error': 'Erreur d\'Authentification',
    'retry_auth': 'Réessayer l\'Authentification',
    'fallback_auth': 'Utilisation de l\'authentification de secours (Firebase non configuré)',
    'demo_mode': 'Utilisation du mode démo (Firebase non configuré)',

    // Errors
    'invalid_name': 'Nom Invalide',
    'enter_valid_name': 'Veuillez entrer un nom valide',
    'auth_required': 'Authentification Requise',
    'failed_create': 'Échec de création',
    'unknown_error': 'Erreur inconnue survenue',
  }
};

export function useTranslation() {
  const { userLanguage } = useUserLanguage();
  const { user } = useAuth();

  const t = (key: string): string => {
    const langTranslations = translations[userLanguage as keyof typeof translations];
    if (langTranslations && langTranslations[key as keyof typeof langTranslations]) {
      return langTranslations[key as keyof typeof langTranslations];
    }
    // Fallback to English
    return translations.en[key as keyof typeof translations.en] || key;
  };

  const translate = async (text: string, targetLang: string) => {
    try {
      const response = await fetch(apiUrl('/api/translate'), {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ text, targetLanguage: targetLang, requesterId: user?.uid }),
      });

      if (response.ok) {
        const result = await response.json();
        return result.translatedText || text;
      }
    } catch (error) {
      console.error('Translation failed:', error);
    }
    return text;
  };

  return { t, translate, userLanguage };
}
